import React, { useState, useEffect } from "react";
import { PencilIcon, EyeIcon, DocumentTextIcon, ArrowLeftIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate } from 'react-router-dom';

const KarPavatiDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTable, setShowTable] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [viewReceipt, setViewReceipt] = useState(false);

  useEffect(() => {
    if (location.state?.paymentData) {
      setPaymentData(location.state.paymentData);
    }
    if (location.state?.userData) {
      setUserData(location.state.userData);
    }
  }, [location.state]);

  // Dynamic tax data based on payment data
  const taxData = paymentData ? [
    { karPrakar: "घरपट्टी", magil: paymentData.taxDetails?.gharpatti?.previous || 0, chalu: paymentData.taxDetails?.gharpatti?.current || 0, ekun: paymentData.taxDetails?.gharpatti?.total || 0, paid: paymentData.taxDetails?.gharpatti?.paid || 0 },
    { karPrakar: "विज कर", magil: paymentData.taxDetails?.vij?.previous || 0, chalu: paymentData.taxDetails?.vij?.current || 0, ekun: paymentData.taxDetails?.vij?.total || 0, paid: paymentData.taxDetails?.vij?.paid || 0 },
    { karPrakar: "आरोग्य कर", magil: paymentData.taxDetails?.aarogya?.previous || 0, chalu: paymentData.taxDetails?.aarogya?.current || 0, ekun: paymentData.taxDetails?.aarogya?.total || 0, paid: paymentData.taxDetails?.aarogya?.paid || 0 },
    { karPrakar: "ग्रामनिधी नोटीस फी", magil: 0, chalu: paymentData.otherCharges?.noticeFee || 0, ekun: paymentData.otherCharges?.noticeFee || 0, paid: paymentData.otherCharges?.noticeFee || 0 },
    { karPrakar: "ग्रामनिधी जप्ती फी", magil: 0, chalu: paymentData.otherCharges?.japtFee || 0, ekun: paymentData.otherCharges?.japtFee || 0, paid: paymentData.otherCharges?.japtFee || 0 },
    { karPrakar: "मागील बाकी दंड", magil: 0, chalu: paymentData.otherCharges?.penalty || 0, ekun: paymentData.otherCharges?.penalty || 0, paid: paymentData.otherCharges?.penalty || 0 },
    { karPrakar: "करामधील सूट", magil: 0, chalu: -(paymentData.otherCharges?.discount || 0), ekun: -(paymentData.otherCharges?.discount || 0), paid: -(paymentData.otherCharges?.discount || 0) },
    { karPrakar: "एकूण", magil: 0, chalu: paymentData.totals?.totalTax || 0, ekun: paymentData.totals?.grandTotal || 0, paid: paymentData.totals?.grandTotal || 0 }
  ] : [];

  const handlePrint = () => {
    window.print();
  };

  const ReceiptView = () => {
    const totalAmount = paymentData?.totals?.grandTotal || 0;

    // Helper to convert number to Marathi words (simplified)
    const numToMarathiWords = (n) => {
      // Create a simplified version or just return text
      return `${n} (अक्षरी)`;
    };

    return (
      <div className="bg-white min-h-screen p-8 text-black font-serif relative">
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .receipt-content, .receipt-content * { visibility: visible; }
            .receipt-content { position: absolute; left: 0; top: 0; width: 100%; }
            .no-print { display: none !important; }
            @page { size: auto; margin: 5mm; }
          }
        `}</style>

        {/* Navigation / Actions */}
        <div className="mb-6 flex justify-between no-print">
          <button
            onClick={() => setViewReceipt(false)}
            className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handlePrint}
            className="bg-[#267c77] text-white px-6 py-2 rounded hover:bg-[#267c77] font-bold"
          >
            Print Receipt
          </button>
        </div>

        {/* Receipt Layout */}
        <div className="receipt-content border-2 border-black p-4 w-full max-w-3xl mx-auto">

          {/* Header */}
          <div className="text-center border-b-2 border-black pb-2 mb-2">
            <h2 className="text-xl font-bold">ग्रामपंचायत इनाम धामणी</h2>
            <p className="text-sm">ता. मिरज, जि. सांगली</p>
            <h1 className="text-2xl font-bold mt-2 underline">कर भरणा पावती</h1>
          </div>

          {/* Info Row 1 */}
          <div className="flex justify-between mb-2 text-sm">
            <div className="w-1/2">
              <p><b>पावती क्र. :</b> {paymentData?.paavatiKr || '---'}</p>
              <p><b>मिळकत क्र. :</b> {paymentData?.maalmataaKr || userData?.id || '---'}</p>
            </div>
            <div className="w-1/2 text-right">
              <p><b>दिनांक :</b> {paymentData?.vasoolDinaank ? new Date(paymentData.vasoolDinaank).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>

          {/* Info Row 2 */}
          <div className="mb-4 text-sm">
            <p><b>श्री / सौ. :</b> {paymentData?.karBharnaaryaacheNaav || userData?.owner || '---'}</p>
            <p><b>राहणार :</b> इनाम धामणी</p>
          </div>

          {/* Table */}
          <table className="w-full border-collapse border border-black mb-4 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black px-2 py-1 text-left w-16">अ.क्र.</th>
                <th className="border border-black px-2 py-1 text-left">तपशील</th>
                <th className="border border-black px-2 py-1 text-right w-32">रक्कम (रु.)</th>
              </tr>
            </thead>
            <tbody>
              {/* Only show relevant items */}
              {taxData.filter(item => item.karPrakar !== "एकूण" && (item.paid !== 0 || item.ekun !== 0)).map((item, index) => (
                <tr key={index}>
                  <td className="border border-black px-2 py-1">{index + 1}</td>
                  <td className="border border-black px-2 py-1">{item.karPrakar}</td>
                  <td className="border border-black px-2 py-1 text-right">{item.paid || 0}</td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="font-bold bg-gray-50">
                <td className="border border-black px-2 py-1" colSpan="2" style={{ textAlign: 'right' }}>एकूण रक्कम</td>
                <td className="border border-black px-2 py-1 text-right">{totalAmount}</td>
              </tr>
            </tbody>
          </table>

          {/* Footer */}
          <div className="mt-4 text-sm">
            <p className="mb-2"><b>अक्षरी रुपये :</b> {numToMarathiWords(totalAmount)}  मात्र.</p>

            <div className="flex justify-between items-end mt-12">
              <div className="text-center">
                <p>मालकाची सही</p>
              </div>
              <div className="text-center">
                <p className="font-bold">ग्रामसेवक / लिपिक</p>
                <p>ग्रामपंचायत इनाम धामणी</p>
              </div>
            </div>
          </div>

          <div className="text-[10px] text-center mt-4 border-t border-gray-400 pt-1">
            हा संगणकीकृत पावती असल्यामुळे सहीची आवश्यकता नाही. (This is a computer generated receipt.)
          </div>

        </div>
      </div>
    );
  };

  if (viewReceipt) {
    return <ReceiptView />;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white border rounded shadow">

        {/* Header */}
        <div className="bg-[#267c77] text-white px-4 py-3 text-lg font-semibold">
          कर पावती सविस्तर माहिती
        </div>

        {/* Property Info */}
        <div className="border m-4 rounded p-4 text-sm">
          <div className="flex justify-between mb-2">
            <div>
              <b>मिळकत क्र.</b> : {paymentData?.maalmataaKr || userData?.id || 'N/A'}
            </div>
            <div>
              <b>भोगवटाधारक</b> : {paymentData?.karBharnaaryaacheNaav || userData?.owner || 'N/A'}
            </div>
          </div>

          <div className="flex justify-between">
            <div>
              <b>मिळकत मालक</b> : {paymentData?.milkatDhaarak || userData?.owner || 'N/A'}
            </div>
            <div>
              <b>मिळकत प्रकार</b> : मूळ मिळकत
            </div>
          </div>
        </div>

        {/* Green Title */}
        <div className="bg-green-600 text-white px-4 py-2 mx-4 rounded">
          पावती माहिती
        </div>

        {/* Receipt Info */}
        <div className="bg-[#267c77] text-white px-4 py-3 mx-4 mt-2 rounded">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <b>पावती नं.</b> : {paymentData?.paavatiKr || 'N/A'}
            </div>
            <div>
              <b>पावती दिनांक</b> : {paymentData?.vasoolDinaank ? new Date(paymentData.vasoolDinaank).toLocaleDateString('en-GB') : 'N/A'}
            </div>
            <div>
              <b>ज्याचे भरणा नाव</b> : {paymentData?.karBharnaaryaacheNaav || 'N/A'}
            </div>
            <div>
              <b>एकूण रक्कम</b> : {paymentData?.totals?.grandTotal || 'N/A'}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-[#267c77] mx-4 my-4 rounded flex justify-between items-center px-4 py-2">

          <button
            onClick={() => setShowTable(!showTable)}
            className="flex items-center gap-2 bg-[#267c77] text-white px-4 py-1 rounded"
          >
            <PencilIcon className="w-4 h-4" />
            सविस्तर
          </button>

          <button
            onClick={() => setViewReceipt(true)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            <DocumentTextIcon className="w-4 h-4" />
            कर पावती
          </button>

          <button className="flex items-center gap-2 bg-red-600 text-white px-4 py-1 rounded">
            <EyeIcon className="w-4 h-4" />
            शोधा
          </button>

        </div>

        {/* Tax Details Table - Show when सविस्तर button is clicked */}
        {showTable && (
          <div className="mx-4 mb-4">
            <div className="bg-white border rounded shadow overflow-hidden">
              <div className="bg-green-600 text-white px-4 py-2 text-sm font-semibold">
                कर माहिती तक्ता
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">कर प्रकार</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">मागील</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">चालू</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">एकूण</th>
                      <th className="border border-gray-300 px-4 py-2 text-center">भरलेली रक्कम</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxData.map((item, index) => (
                      <tr key={index} className={item.karPrakar === "एकूण" ? "bg-gray-100 font-bold" : "hover:bg-gray-50"}>
                        <td className="border border-gray-300 px-4 py-2">{item.karPrakar}</td>
                        <td className="border border-gray-300 px-4 py-2 text-center">{item.magil}</td>
                        <td className="border border-gray-300 px-4 py-2 text-center">{item.chalu}</td>
                        <td className="border border-gray-300 px-4 py-2 text-center">{item.ekun}</td>
                        <td className="border border-gray-300 px-4 py-2 text-center">{item.paid || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default KarPavatiDetail;
